package io.horion.cv.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronRight
import com.varabyte.kobweb.silk.components.icons.fa.FaStar
import com.varabyte.kobweb.silk.components.icons.fa.FaStarAndCrescent
import com.varabyte.kobweb.silk.components.icons.fa.FaStarHalf
import com.varabyte.kobweb.silk.components.icons.fa.FaStarHalfStroke
import com.varabyte.kobweb.silk.components.icons.fa.FaStarOfDavid
import com.varabyte.kobweb.silk.components.icons.fa.FaStarOfLife
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.components.TestimonialCard
import io.horion.cv.utils.Colors
import io.horion.cv.models.TestimonialData
import io.horion.cv.styles.TestimonialDesktopContainerStyle
import io.horion.cv.styles.TestimonialDesktopRowStyle
import io.horion.cv.styles.TestimonialMobileContainerStyle
import io.horion.cv.styles.TestimonialSectionStyle
import io.horion.cv.styles.TestimonialTitleStyle
import io.horion.cv.utils.Constants.POPPINS
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import kotlin.time.Duration.Companion.milliseconds

@OptIn(DelicateApi::class)
@Composable
fun TestimonialSection() {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark
    val sectionBg = if (isDark) Colors.DarkBG else Colors.White

    Column(
        modifier = TestimonialSectionStyle.toModifier()
            .fillMaxWidth()
            .backgroundColor(sectionBg)
            .styleModifier { property("transition", "background-color 300ms ease") },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            "Les avis de nos utilisateurs",
            modifier = TestimonialTitleStyle.toModifier()
                .color(Colors.DarkBlue)
                .fontFamily(POPPINS)
                .fontWeight(FontWeight.SemiBold)
                .styleModifier { property("text-align", "center") }
        )

        Box(modifier = TestimonialMobileContainerStyle.toModifier()) {
            TestimonialCarousel()
        }

        // Affichage Desktop (Grid/Row)
        Row(
            modifier = TestimonialDesktopContainerStyle.toModifier()
                .then(TestimonialDesktopRowStyle.toModifier()),
            horizontalArrangement = Arrangement.SpaceEvenly
        ) {
            TestimonialData.testimonials.forEach { testimonial ->
                TestimonialCard(item = testimonial)
            }
        }

    }
}

@Composable
fun TestimonialCarousel() {
    val items = TestimonialData.testimonials
    val pageCount = items.size
    var page by remember { mutableStateOf(0) }
    var targetPage by remember { mutableStateOf(0) }
    var animating by remember { mutableStateOf(false) }
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val chevronBg = if (isDark) Colors.DarkBG else Colors.LighterBlue
    val navBg     = if (isDark) Colors.DarkBG else Colors.GrayLight

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 20.px),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Relative)
                .overflow(Overflow.Hidden),
            contentAlignment = Alignment.Center
        ) {
            if (page > 0) {
                Box(
                    modifier = Modifier
                        .size(28.px)
                        .borderRadius(40.px)
                        .cursor(Cursor.Pointer)
                        .backgroundColor(Colors.LighterBlue)
                        .position(Position.Absolute)
                        .left(30.px)
                        .styleModifier { property("z-index", "10")
                            property("transition", "background-color 300ms ease")
                        }
                        .onClick {
                            if (!animating) { targetPage = page - 1; animating = true }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    FaChevronLeft(
                        modifier = Modifier
                            .fontSize(18.px)
                            .color(Colors.White)
                    )
                }
            }

            Box(
                modifier = Modifier
                    .width(100.percent)
                    .overflow(Overflow.Hidden)
            ) {
                Row(
                    modifier = Modifier
                        .width((pageCount * 100).percent)
                        .styleModifier {
                            property("transform", "translateX(-${targetPage * (100.0 / pageCount)}%)")
                            property("transition", "transform 400ms ease")
                        }
                ) {
                    items.forEach { testimonial ->
                        Box(
                            modifier = Modifier
                                .width((100.0 / pageCount).percent)
                                .padding(leftRight = 40.px),
                            contentAlignment = Alignment.Center
                        ) {
                            TestimonialCard(item = testimonial)
                        }
                    }
                }
            }

            if (page < pageCount - 1) {
                Box(
                    modifier = Modifier
                        .size(28.px)
                        .borderRadius(40.px)
                        .cursor(Cursor.Pointer)
                        .backgroundColor(Colors.LighterBlue)
                        .position(Position.Absolute)
                        .right(30.px)
                        .styleModifier { property("z-index", "10")
                            property("transition", "background-color 300ms ease")
                        }
                        .onClick {
                            if (!animating) { targetPage = page + 1; animating = true }
                        },
                    contentAlignment = Alignment.Center
                ) {
                    FaChevronRight(
                        modifier = Modifier.fontSize(18.px)
                            .color(Colors.White)
                    )
                }
            }
        }

        if (animating) {
            LaunchedEffect(targetPage) {
                delay(400. milliseconds)
                page = targetPage
                animating = false
            }
        }

        Box(
            modifier = Modifier
                .margin(top = 16.px)
                .padding(leftRight = 8.px, topBottom = 6.px)
                .borderRadius(50.px)
                .backgroundColor(navBg)
                .styleModifier { property("transition", "background-color 300ms ease") },
            contentAlignment = Alignment.Center
        ) {
            Row(
                modifier = Modifier.gap(8.px),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                repeat(pageCount) { index ->
                    val dotColor = when {
                        index == page -> Colors.LighterBlue
                        isDark -> Colors.gray
                        else -> Colors.LightGray
                    }

                    Box(
                        modifier = Modifier
                            .size(6.px)
                            .borderRadius(50.px)
                            .backgroundColor(dotColor)
                            .styleModifier { property("transition", "background-color 300ms ease") }
                            .cursor(Cursor.Pointer)
                            .onClick {
                                if (!animating && index != page) { targetPage = index; animating = true }
                            }
                    )
                }
            }
        }
    }
}